// Dashboard, chat, and finance stay in the client and ship dormant.
// Build-time switches default off. /api/check_access and /api/app-context
// may send features and that payload overrides the build.

export const TRIPS_HOME_PATH = "/trips";

export const DORMANT_FEATURES_OFF = {
  dashboard: false,
  chat: false,
  finance: false,
};

export const DORMANT_FEATURES_ON = {
  dashboard: true,
  chat: true,
  finance: true,
};

const TRUTHY = new Set(["1", "true", "yes", "on"]);

let runtimeFeatures = null;
const listeners = new Set();

export function parseDormantFlag(value) {
  if (value === true) return true;
  return TRUTHY.has(String(value ?? "").trim().toLowerCase());
}

export function readBuildTimeDormantFeatures() {
  return {
    dashboard: parseDormantFlag(process.env.REACT_APP_DOPLE_DASHBOARD_ENABLED),
    chat: parseDormantFlag(process.env.REACT_APP_DOPLE_CHAT_ENABLED),
    finance: parseDormantFlag(process.env.REACT_APP_DOPLE_FINANCE_ENABLED),
  };
}

function flagFromPayload(source, baseValue, shortName, serverName) {
  if (Object.prototype.hasOwnProperty.call(source, serverName)) return parseDormantFlag(source[serverName]);
  if (Object.prototype.hasOwnProperty.call(source, shortName)) return parseDormantFlag(source[shortName]);
  return Boolean(baseValue);
}

// /api/app-context sends dashboard_enabled, chat_enabled, and finance_enabled.
// Short names are accepted so tests and older payloads still apply.
export function normalizeDormantFeatures(payload, base = DORMANT_FEATURES_OFF) {
  const source = payload && typeof payload === "object" ? payload : {};
  return {
    dashboard: flagFromPayload(source, base.dashboard, "dashboard", "dashboard_enabled"),
    chat: flagFromPayload(source, base.chat, "chat", "chat_enabled"),
    finance: flagFromPayload(source, base.finance, "finance", "finance_enabled"),
  };
}

function sameFeatures(left, right) {
  return Boolean(
    left &&
      right &&
      left.dashboard === right.dashboard &&
      left.chat === right.chat &&
      left.finance === right.finance
  );
}

export function getDormantFeatures() {
  return runtimeFeatures || readBuildTimeDormantFeatures();
}

export function applyRuntimeDormantFeatures(payload) {
  if (!payload || typeof payload !== "object") return getDormantFeatures();
  const next = normalizeDormantFeatures(payload, getDormantFeatures());
  if (sameFeatures(runtimeFeatures, next)) return runtimeFeatures;
  runtimeFeatures = next;
  listeners.forEach((listener) => listener(next));
  return next;
}

export function subscribeDormantFeatures(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetDormantFeaturesForTests() {
  runtimeFeatures = null;
  listeners.clear();
}

const FINANCE_PREFIXES = [
  "/dashboard/finances",
  "/dashboard/budgets",
  "/dashboard/transactions",
  "/dashboard/subscriptions",
  "/dashboard/spending",
  "/dashboard/loans",
  "/budget",
];

export function productFeatureForPath(value) {
  const next = String(value || "").trim();
  if (!next.startsWith("/") || next.startsWith("//")) return "";
  const path = next.split("?")[0].split("#")[0];
  if (FINANCE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return "finance";
  }
  if (path === "/chat" || path.startsWith("/chat/")) return "chat";
  if (path === "/dashboard" || path.startsWith("/dashboard/")) return "dashboard";
  return "";
}

export function isDormantProductPath(value, features = getDormantFeatures()) {
  const feature = productFeatureForPath(value);
  if (!feature) return false;
  return !features[feature];
}

// Routes that come back with a flag. Kept here so tests can prove the
// original paths still exist and which flag reveals each one.
export const DORMANT_CLIENT_ROUTES = [
  { path: "/dashboard", feature: "dashboard" },
  { path: "/dashboard/comingsoon", feature: "dashboard" },
  { path: "/dashboard/mymodel", feature: "dashboard" },
  { path: "/dashboard/finances", feature: "finance" },
  { path: "/dashboard/budgets", feature: "finance" },
  { path: "/dashboard/transactions", feature: "finance" },
  { path: "/dashboard/subscriptions", feature: "finance" },
  { path: "/dashboard/spending", feature: "finance" },
  { path: "/budget/create", feature: "finance" },
  { path: "/budget/:budgetId", feature: "finance" },
  { path: "/budget/:budgetId/edit", feature: "finance" },
  { path: "/chat", feature: "chat" },
];

export const PUBLIC_SITE_PATHS = [
  "/",
  "/about",
  "/contact",
  "/privacy",
  "/terms",
  "/guides",
  "/guides/:slug",
  "/login",
  "/signup",
  "/samples",
  "/samples/*",
  "/compare",
  "/compare/dople-vs-mindtrip",
  "/compare/:slug",
  "/use-cases",
  "/use-cases/:slug",
  "/llms.txt",
  "/llms-full.txt",
  "/robots.txt",
  "/sitemap.xml",
  "/terms-of-service.pdf",
  "/privacy-policy.pdf",
  "/subscribe",
  "/survey",
  "/stripe/success",
];
